import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SaasEditOpportunitySection from "../SaasEditOpportunitySection/SaasEditOpportunitySection"
import SaasRoleManagementSection from "../SaasRoleManagementSection/SaasRoleManagementSection"
import SaasOverviewSection from "../SaasOverviewSection/SaasOverviewSection"
import SaasUploadOpportunitySection from "../SaasUploadOpportunitySection/SaasUploadOpportunitySection"
import SaasUserManagementSection from "../SaasUserManagementSection/SaasUserManagementSection"
import SaasReviewOpportunitiesSection from "../SaasReviewOpportunitiesSection/SaasReviewOpportunitiesSection"
import SaasPhdConnectSection from "./SaasPhdConnectSection"
import { OpportunityService } from "../../services/OpportunityService";
import { encodeImageUrl } from "../../utils/helpers";
import { hasPermission, canManageUsers, canManageRoles, canManageOpportunities } from "../../utils/permissions";
import { saasApiAxios } from "../../lib/axios-interceptor";
import { getLoginPath } from "../../utils/environment";
import { 
  FaHome,
  FaPlus,
  FaEdit,
  FaUsers,
  FaUserCheck,
  FaShieldAlt,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaCog,
  FaBell,
  FaSearch,
  FaChevronDown,
  FaBookOpen,
  FaUpload,
  FaFileAlt,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaDollarSign,
  FaClock,
  FaFilter,
  FaEllipsisV,
  FaEye,
  FaTrash,
  FaUserPlus,
  FaEnvelope,
  FaPhone,
  FaCheckCircle,
  FaExclamationCircle,
  FaTrendingUp,
  FaGraduationCap,
  FaUniversity,
  FaLink
} from 'react-icons/fa';

const SaaSDashboard = () => {
  const location = useLocation();
  const [selectedSection, setSelectedSection] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Sample data
  const [opportunities, setOpportunities] = useState([
    {
      id: 1,
      title: 'Machine Learning for Climate Change',
      department: 'Computer Science',
      supervisor: 'Prof. James Mitchell',
      deadline: '2025-08-15',
      funding: 'Full funding available',
      status: 'Active',
      applicants: 12
    },
    {
      id: 2,
      title: 'Medieval Literature Analysis',
      department: 'English Literature',
      supervisor: 'Dr. Emma Thompson',
      deadline: '2025-07-30',
      funding: 'Partial funding',
      status: 'Active',
      applicants: 8
    }
  ]);

  const [users, setUsers] = useState([
    {
      id: 1,
      name: 'Prof. James Mitchell',
      email: 'j.mitchell@oxford.ac.uk',
      role: 'Supervisor',
      department: 'Computer Science',
      status: 'Active'
    },
    {
      id: 2,
      name: 'Dr. Emma Thompson',
      email: 'e.thompson@oxford.ac.uk',
      role: 'Supervisor',
      department: 'English Literature',
      status: 'Active'
    }
  ]);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        const tenantStr = localStorage.getItem('tenant');

        if (!token || !userStr || !tenantStr) {
          // Redirect to login if missing auth data
          const hostname = window.location.hostname;
          const isSaaSSubdomain = hostname !== 'phdscanner.com' && 
                                 hostname !== 'www.phdscanner.com' &&
                                 !hostname.includes('localhost');
          
          window.location.href = getLoginPath();
          return;
        }

        // Parse user data
        const userData = JSON.parse(userStr);
        setUser(userData);
        setTenant(tenantStr);
        setIsAuthenticated(true);

        // Fetch customer information
        try {
          const customerResponse = await OpportunityService.getCurrentCustomer();
          if (customerResponse && !customerResponse.error) {
            setCustomerInfo(customerResponse);
          }
        } catch (error) {
          console.error('Failed to fetch customer info:', error);
          // Continue without customer info
        }

        setIsLoading(false);

        // Optional: Verify token validity with backend
        // verifyToken(token);

      } catch (error) {
        console.error('Authentication check failed:', error);
        // Clear invalid data and redirect
        clearAuthData();
        
        const hostname = window.location.hostname;
        const isSaaSSubdomain = hostname !== 'phdscanner.com' && 
                               hostname !== 'www.phdscanner.com' &&
                               !hostname.includes('localhost');
        
        window.location.href = getLoginPath();
      }
    };

    checkAuth();
  }, []);

  // Handle navigation state to set the correct section
  useEffect(() => {
    if (location.state?.section) {
      setSelectedSection(location.state.section);
    }
  }, [location.state]);

  // Clear authentication data
  const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    setUser(null);
    setTenant('');
    setIsAuthenticated(false);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      // Optional: Call logout API endpoint
      try {
        await saasApiAxios.post('/api/auth/logout');
      } catch (error) {
        console.error('Logout API call failed:', error);
        // Continue with local logout even if API fails
      }
    } finally {
      // Always clear local data and redirect
      clearAuthData();
      
      const hostname = window.location.hostname;
      const isSaaSSubdomain = hostname !== 'phdscanner.com' && 
                             hostname !== 'www.phdscanner.com' &&
                             !hostname.includes('localhost');
      
      window.location.href = getLoginPath();
    }
  };

  // Optional: Verify token with backend
  const verifyToken = async (token) => {
    try {
      const response = await saasApiAxios.get('/api/auth/verify');
      const data = response.data;
      // Update user data if needed
      if (data.user) {
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      clearAuthData();
      window.location.href = getLoginPath();
    }
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFEEED] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DB4F40] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1B222A]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated (this should rarely show due to useEffect redirect)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FFEEED] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#1B222A] mb-4">You need to be logged in to access this page.</p>
          <button
            onClick={() => {
              window.location.href = getLoginPath();
            }}
            className="px-6 py-3 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.email || 'User';
  };

  // Get user initials
  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user?.email?.charAt(0)?.toUpperCase() || 'U';
  };

  // Check if user has permission
  const checkPermission = (permission) => {
    const hasPerm = hasPermission(user, permission);
    return hasPerm;
  };

  const menuItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: FaHome,
      description: 'Dashboard overview',
      permission: null // Always accessible
    },
    {
      id: 'phd-connect',
      label: 'PhD Connect',
      icon: FaLink,
      description: 'Import opportunities from main platform',
      permission: 'manage_opportunities'
    },
    {
      id: 'upload-opportunity',
      label: 'Upload Opportunity',
      icon: FaPlus,
      description: 'Add PhD opportunities',
      permission: 'manage_opportunities'
    },
    {
      id: 'review-opportunities',
      label: 'Review Opportunities',
      icon: FaBookOpen,
      description: 'Review candidate applications',
      permission: 'manage_opportunities'
    },
    {
      id: 'user-management',
      label: 'User Management',
      icon: FaUsers,
      description: 'Manage users and permissions',
      permission: 'manage_users'
    },
    {
      id: 'role-management',
      label: 'Role Management',
      icon: FaShieldAlt,
      description: 'Configure user roles',
      permission: 'role_management'
    }
  ];

  // Show all menu items for all users (read-only access)
  // Actions within each section will be controlled by permissions
  const filteredMenuItems = menuItems;
  
  const Sidebar = () => (
    <aside
      className={`
        fixed md:fixed inset-y-0 left-0 w-full md:w-80
        bg-white/95 backdrop-blur-sm shadow-lg z-50 transform transition-transform duration-300
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0 border-r border-[#FFD6D4] flex flex-col
        md:rounded-none rounded-r-2xl md:shadow-lg shadow-2xl
      `}
      style={{ maxWidth: '100vw' }}
    >
    
      {/* Tenant Info */}
      <div className="p-6 border-b border-[#FFD6D4] bg-[#FFEEED]/50">
        <div className="flex items-center">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mr-4 overflow-hidden bg-white shadow-sm">
            <img 
              src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : ''}/university/${encodeImageUrl(customerInfo?.university || tenant)}.png`}
              alt={customerInfo?.university || tenant}
              className="w-12 h-12 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <FaUniversity className="w-12 h-12 text-[#DB4F40] hidden" />
          </div>
          <div>
            <h3 className="font-semibold text-[#1B222A] capitalize text-base md:text-lg">{customerInfo?.university || tenant}</h3>
            <p className="text-xs md:text-sm text-[#1B222A]/70 italic">Powered by PhDScanner</p>
          </div>
        </div>
      </div>
      {/* Navigation Menu */}
      <nav className="flex-1 p-4 bg-white/80 overflow-y-auto">
        <div className="space-y-2">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setSelectedSection(item.id);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center p-4 md:p-3 rounded-xl transition-all duration-200 group text-base md:text-sm gap-2 md:gap-0.5
                  ${selectedSection === item.id
                    ? 'bg-[#FFD6D4] text-[#DB4F40] border border-[#DB4F40]/20 shadow-md'
                    : 'text-[#1B222A] hover:bg-[#FFEEED] hover:text-[#DB4F40]'}
                `}
                style={{ minHeight: '3.5rem' }}
              >
                <Icon className={`w-6 h-6 md:w-5 md:h-5 mr-3 ${
                  selectedSection === item.id ? 'text-[#DB4F40]' : 'text-[#1B222A]/60 group-hover:text-[#DB4F40]'
                }`} />
                <div className="text-left">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs text-[#1B222A]/70 hidden sm:block">{item.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </nav>
      {/* User Info & Logout */}
      <div className="p-4 border-t border-[#FFD6D4] bg-white/90 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-r from-[#DB4F40] to-[#c13426] rounded-full flex items-center justify-center mr-3 shadow-md">
              <span className="text-white font-medium">
                {getUserInitials()}
              </span>
            </div>
            <div>
              <p className="font-medium text-[#1B222A] truncate text-sm md:text-base">{getUserDisplayName()}</p>
              <p className="text-xs text-[#1B222A]/70 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-[#1B222A]/60 hover:text-[#DB4F40] transition-colors p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#DB4F40]"
            title="Logout"
          >
            <FaSignOutAlt className="w-5 h-5" />
          </button>
        </div>
      </div>
    </aside>
  );

  const Header = () => (
    <header className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-[#FFD6D4] md:hidden sticky top-0 z-40">
      <div className="flex items-center justify-between p-4">
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="text-[#1B222A]/60 hover:text-[#DB4F40] transition-colors p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#DB4F40]"
        >
          <FaBars className="h-7 w-7" />
        </button>
        <div className="flex items-center justify-center flex-1">
          <img 
            src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : ''}/university/${encodeImageUrl(customerInfo?.university || tenant)}.png`}
            alt="University Logo" 
            className="w-12 h-12 md:w-12 md:h-12 mr-2 rounded object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <FaUniversity className="w-12 h-12 md:w-12 md:h-12 text-[#DB4F40] mr-2 hidden" />
          <span className="font-bold text-3xl md:text-3xl text-[#1B222A] capitalize tracking-tight">{customerInfo?.university || tenant}</span>
        </div>
        <div className="w-7"></div>
      </div>
    </header>
  );

  const renderContent = () => {
    switch (selectedSection) {
      case 'overview':
        return <SaasOverviewSection />;
      case 'phd-connect':
        return <SaasPhdConnectSection />;
      case 'upload-opportunity':
        return <SaasUploadOpportunitySection />;
      case 'edit-opportunity':
        return <SaasEditOpportunitySection />;
      case 'review-opportunities':
        return <SaasReviewOpportunitiesSection />;
      case 'user-management':
        return <SaasUserManagementSection />;
      case 'role-management':
        return <SaasRoleManagementSection />;
      default:
        return <SaasOverviewSection />;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFEEED] flex">
      {/* Floating background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-[#FFEEED] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-3/4 w-80 h-80 bg-[#FFD6D4] rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-pulse delay-500"></div>
      </div>

      {/* Sidebar */}
      <Sidebar />
      
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 md:ml-80">
        <Header />
        
        <main className="flex-1 p-6 overflow-y-auto bg-[#FFEEED]/30">
          <div className="relative z-10">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SaaSDashboard;